import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  AreaSeries,
  CandlestickSeries,
  ColorType,
  createChart,
  CrosshairMode,
  type AreaData,
  type AreaSeriesPartialOptions,
  type CandlestickData,
  type CandlestickSeriesPartialOptions,
  type DeepPartial,
  type IChartApi,
  type ISeriesApi,
  LineStyle,
  type Time,
  type TimeChartOptions,
  type UTCTimestamp,
} from "lightweight-charts";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Candlestick, Market } from "@module-bindings/types";
import { assetIdKey, fixQuery, formatMarketPairLabel } from "@/lib/marketUtils";
import { TradingPaneState } from "./TradingPaneState";

const FRAC_BITS = 60n;
const DENOM = 1n << FRAC_BITS;

const CHART_COLORS = {
  background: "#22141b",
  border: "#54504b",
  surface: "#2a2622",
  text: "#8a857e",
  positive: "#febbe2",
  gain: "#72f082",
  gainWick: "rgba(114, 240, 130, 0.72)",
  loss: "#ff5b5b",
  lossWick: "rgba(255, 91, 91, 0.72)",
  crosshair: "rgba(189, 184, 176, 0.32)",
} as const;

const DEFAULT_SCALE_MARGINS = {
  top: 0.15,
  bottom: 0.12,
} as const;

const MAX_CANDLES: Record<Timeframe, number> = {
  "1s": 120,
  "1m": 120,
  "1h": 168,
  "1d": 90,
};

const TF_SECONDS: Record<Timeframe, bigint> = {
  "1s": 1n,
  "1m": 60n,
  "1h": 3600n,
  "1d": 86400n,
};

type ChartMode = "line" | "candle";
type Timeframe = "1s" | "1m" | "1h" | "1d";

interface PriceChartProps {
  compact?: boolean;
  immersiveMobile?: boolean;
  selectedMarket: Market | null;
  markets?: Market[];
  onSelectMarket?: (market: Market) => void;
}

function priceToNumber(raw: bigint): number {
  return Number(raw >> FRAC_BITS) + Number(raw & (DENOM - 1n)) / Number(DENOM);
}

function formatPrice(value: number): string {
  const abs = Math.abs(value);
  const maximumFractionDigits = abs >= 100 ? 2 : abs >= 1 ? 4 : 6;

  return value.toLocaleString(undefined, {
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits,
  });
}

function getChartOptions(
  compact: boolean,
  timeframe: Timeframe,
): DeepPartial<TimeChartOptions> {
  return {
    autoSize: true,
    layout: {
      background: {
        type: ColorType.Solid,
        color: CHART_COLORS.background,
      },
      attributionLogo: false,
      textColor: CHART_COLORS.text,
      fontFamily: "Geist, sans-serif",
    },
    grid: {
      vertLines: { color: "transparent" },
      horzLines: { color: "transparent" },
    },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: {
        color: CHART_COLORS.crosshair,
        width: 1,
        style: LineStyle.Dotted,
        labelBackgroundColor: CHART_COLORS.surface,
      },
      horzLine: {
        color: CHART_COLORS.crosshair,
        width: 1,
        style: LineStyle.Dotted,
        labelBackgroundColor: CHART_COLORS.surface,
      },
    },
    handleScroll: {
      mouseWheel: true,
      pressedMouseMove: true,
      horzTouchDrag: true,
      vertTouchDrag: true,
    },
    handleScale: {
      mouseWheel: true,
      pinch: true,
      axisPressedMouseMove: true,
      axisDoubleClickReset: true,
    },
    kineticScroll: {
      mouse: true,
      touch: true,
    },
    localization: {
      priceFormatter: formatPrice,
    },
    leftPriceScale: {
      visible: false,
      borderColor: CHART_COLORS.border,
    },
    rightPriceScale: {
      visible: !compact,
      borderColor: CHART_COLORS.border,
      textColor: CHART_COLORS.text,
      scaleMargins: DEFAULT_SCALE_MARGINS,
    },
    timeScale: {
      visible: !compact,
      borderColor: CHART_COLORS.border,
      timeVisible: true,
      secondsVisible: timeframe === "1s",
      rightBarStaysOnScroll: true,
      lockVisibleTimeRangeOnResize: true,
      fixLeftEdge: true,
      rightOffset: compact ? 4 : 8,
    },
  };
}

function getAreaSeriesOptions(strokeColor: string): AreaSeriesPartialOptions {
  return {
    visible: true,
    lineColor: strokeColor,
    topColor:
      strokeColor === CHART_COLORS.loss
        ? "rgba(255, 91, 91, 0.18)"
        : "rgba(254, 187, 226, 0.18)",
    bottomColor: "rgba(23, 11, 16, 0)",
    lineWidth: 2 as const,
    priceLineVisible: false,
    lastValueVisible: false,
    crosshairMarkerBorderColor: strokeColor,
    crosshairMarkerBackgroundColor: CHART_COLORS.background,
    priceFormat: {
      type: "custom" as const,
      minMove: 0.000001,
      formatter: formatPrice,
    },
  };
}

function getCandlestickSeriesOptions(): CandlestickSeriesPartialOptions {
  return {
    visible: false,
    upColor: CHART_COLORS.gain,
    downColor: CHART_COLORS.loss,
    wickUpColor: CHART_COLORS.gainWick,
    wickDownColor: CHART_COLORS.lossWick,
    borderVisible: false,
    priceLineVisible: false,
    lastValueVisible: false,
    priceFormat: {
      type: "custom" as const,
      minMove: 0.000001,
      formatter: formatPrice,
    },
  };
}

export const PriceChart: FC<PriceChartProps> = ({
  compact = false,
  immersiveMobile = false,
  selectedMarket,
}) => {
  const [chartMode, setChartMode] = useState<ChartMode>("line");
  const [timeframe, setTimeframe] = useState<Timeframe>("1s");

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const areaSeriesRef = useRef<ISeriesApi<"Area", Time> | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<"Candlestick", Time> | null>(
    null,
  );
  const lastAutoFitKeyRef = useRef<string | null>(null);

  const pairLabel = selectedMarket
    ? formatMarketPairLabel(selectedMarket)
    : "No Markets";
  const pairKey = selectedMarket
    ? `${assetIdKey(selectedMarket.tradingPair.base)}/${assetIdKey(
        selectedMarket.tradingPair.quote,
      )}`
    : "";

  const query = fixQuery(
    tables.candlesticks.where((c: any) => c.pairKey.eq(pairKey)),
    [["pairKey", "pair_key"]],
  );
  const [allCandles] = useTable(query) as unknown as [Candlestick[]];

  const candles = useMemo(() => {
    if (!pairKey) {
      return [];
    }

    const sorted = allCandles
      .filter((c: Candlestick) => c.granularity === timeframe)
      .sort(
        (a: Candlestick, b: Candlestick) => Number(a.timestamp - b.timestamp),
      );
    if (sorted.length === 0) {
      return [];
    }

    const filled: Candlestick[] = [sorted[0]];
    const step = TF_SECONDS[timeframe];

    for (let i = 1; i < sorted.length; i += 1) {
      const prev = filled[filled.length - 1];
      const curr = sorted[i];
      let expected = prev.timestamp + step;

      while (expected < curr.timestamp) {
        filled.push({
          ...prev,
          timestamp: expected,
          open: prev.close,
          high: prev.close,
          low: prev.close,
          close: prev.close,
          volume: 0n,
        });
        expected += step;

        if (filled.length > MAX_CANDLES[timeframe] * 2) {
          break;
        }
      }

      filled.push(curr);
    }

    const limit = MAX_CANDLES[timeframe];
    return filled.length > limit ? filled.slice(filled.length - limit) : filled;
  }, [allCandles, pairKey, timeframe]);

  const latestCandle = candles.length > 0 ? candles[candles.length - 1] : null;
  const firstCandle = candles.length > 0 ? candles[0] : null;
  const currentPrice = latestCandle ? priceToNumber(latestCandle.close) : 0;
  const openPrice = firstCandle ? priceToNumber(firstCandle.open) : currentPrice;
  const priceChange = openPrice > 0 ? ((currentPrice - openPrice) / openPrice) * 100 : 0;
  const strokeColor =
    priceChange >= 0 ? CHART_COLORS.positive : CHART_COLORS.loss;

  const candlestickData = useMemo<CandlestickData<Time>[]>(() => {
    return candles.map((c) => ({
      time: Number(c.timestamp) as UTCTimestamp,
      open: priceToNumber(c.open),
      high: priceToNumber(c.high),
      low: priceToNumber(c.low),
      close: priceToNumber(c.close),
    }));
  }, [candles]);

  const areaData = useMemo<AreaData<Time>[]>(() => {
    return candlestickData.map(({ close, time }) => ({
      time,
      value: close,
    }));
  }, [candlestickData]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || chartRef.current) {
      return;
    }

    const chart = createChart(container, getChartOptions(compact, timeframe));
    const areaSeries = chart.addSeries(
      AreaSeries,
      getAreaSeriesOptions(strokeColor),
    );
    const candlestickSeries = chart.addSeries(
      CandlestickSeries,
      getCandlestickSeriesOptions(),
    );

    areaSeries.priceScale().applyOptions({
      scaleMargins: DEFAULT_SCALE_MARGINS,
    });
    candlestickSeries.priceScale().applyOptions({
      scaleMargins: DEFAULT_SCALE_MARGINS,
    });

    chartRef.current = chart;
    areaSeriesRef.current = areaSeries;
    candlestickSeriesRef.current = candlestickSeries;

    let isDragging = false;
    let startX = 0;
    let startY = 0;

    const resetViewport = () => {
      chart.timeScale().resetTimeScale();
      chart.timeScale().fitContent();
      chart.priceScale("right").applyOptions({
        autoScale: true,
        scaleMargins: DEFAULT_SCALE_MARGINS,
      });
    };

    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 2) {
        return;
      }

      isDragging = true;
      startX = event.clientX;
      startY = event.clientY;
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging) {
        return;
      }

      const logicalRange = chart.timeScale().getVisibleLogicalRange();
      if (!logicalRange) {
        return;
      }

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const span = logicalRange.to - logicalRange.from;
      const xZoom = span * deltaX * 0.002;
      const nextRange = {
        from: logicalRange.from + xZoom,
        to: logicalRange.to - xZoom,
      };

      if (nextRange.to - nextRange.from > 2) {
        chart.timeScale().setVisibleLogicalRange(nextRange);
      }

      const priceScale = chart.priceScale("right");
      const currentMargins =
        priceScale.options().scaleMargins ?? DEFAULT_SCALE_MARGINS;
      const yZoom = deltaY * 0.0015;
      const nextTop = Math.max(0.02, Math.min(0.45, currentMargins.top + yZoom));
      const nextBottom = Math.max(
        0.02,
        Math.min(0.45, currentMargins.bottom + yZoom),
      );

      priceScale.applyOptions({
        autoScale: false,
        scaleMargins: {
          top: nextTop,
          bottom: nextBottom,
        },
      });

      startX = event.clientX;
      startY = event.clientY;
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (event.button === 2) {
        isDragging = false;
      }
    };

    container.addEventListener("contextmenu", handleContextMenu);
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("dblclick", resetViewport);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("contextmenu", handleContextMenu);
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("dblclick", resetViewport);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      lastAutoFitKeyRef.current = null;
      candlestickSeriesRef.current = null;
      areaSeriesRef.current = null;
      chartRef.current = null;
      chart.remove();
    };
  }, [compact, selectedMarket?.key]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    chart.applyOptions(getChartOptions(compact, timeframe));
  }, [compact, timeframe]);

  useEffect(() => {
    const areaSeries = areaSeriesRef.current;
    if (!areaSeries) {
      return;
    }

    areaSeries.applyOptions(getAreaSeriesOptions(strokeColor));
  }, [strokeColor]);

  useEffect(() => {
    const areaSeries = areaSeriesRef.current;
    const candlestickSeries = candlestickSeriesRef.current;
    if (!areaSeries || !candlestickSeries) {
      return;
    }

    areaSeries.setData(areaData);
    candlestickSeries.setData(candlestickData);
  }, [areaData, candlestickData]);

  useEffect(() => {
    if (candlestickData.length === 0) {
      return;
    }

    const autoFitKey = `${selectedMarket?.key ?? "none"}:${timeframe}`;
    if (lastAutoFitKeyRef.current === autoFitKey) {
      return;
    }

    lastAutoFitKeyRef.current = autoFitKey;
    chartRef.current?.timeScale().fitContent();
    chartRef.current?.priceScale("right").applyOptions({
      autoScale: true,
      scaleMargins: DEFAULT_SCALE_MARGINS,
    });
  }, [candlestickData.length, selectedMarket?.key, timeframe]);

  useEffect(() => {
    const areaSeries = areaSeriesRef.current;
    const candlestickSeries = candlestickSeriesRef.current;
    if (!areaSeries || !candlestickSeries) {
      return;
    }

    areaSeries.applyOptions({ visible: chartMode === "line" });
    candlestickSeries.applyOptions({ visible: chartMode === "candle" });
  }, [chartMode]);

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <TradingPaneState
          eyebrow="Market View"
          title="Select a market"
          message="Choose a trading pair to load the chart and market context."
        />
      </div>
    );
  }

  return (
    <section
      className={`sr-panel flex h-full flex-col ${compact ? "" : "min-h-0"}`}
      aria-label={`${pairLabel} price chart`}
    >
      <div
        className={
          compact
            ? "px-2 pb-2"
            : immersiveMobile
              ? "relative flex-1 px-1 pt-3 pb-2"
              : "relative min-h-0 flex-1 px-1 pb-1"
        }
      >
        <div
          className={
            compact
              ? "relative h-[120px] w-full overflow-hidden rounded-[18px] bg-sr-surface"
              : immersiveMobile
                ? "relative h-[46vh] min-h-[320px] w-full overflow-hidden rounded-[18px] bg-sr-surface"
                : "relative h-full w-full overflow-hidden rounded-[18px] bg-sr-surface"
          }
        >
          {!compact && (
            <div className="pointer-events-none absolute left-4 top-4 z-10">
              <div className="pointer-events-auto flex items-center gap-1.5 rounded-[16px] border border-white/[0.04] bg-sr-surface/82 px-2 py-2 backdrop-blur">
                {(["1s", "1m", "1h", "1d"] as const).map((tf) => (
                  <button
                    key={tf}
                    onClick={() => setTimeframe(tf)}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
                      timeframe === tf
                        ? "bg-sr-neutral-900 text-sr-lemon-muted border-sr-border-subtle"
                        : "text-sr-neutral-500 border-transparent hover:text-sr-neutral-300"
                    }`}
                  >
                    {tf.toUpperCase()}
                  </button>
                ))}
                <div className="mx-1 h-5 w-px bg-sr-border-subtle" />
                <button
                  onClick={() => setChartMode("line")}
                  className={`sr-focus-ring rounded-md p-2 transition-colors ${
                    chartMode === "line"
                      ? "text-sr-lemon-muted bg-sr-neutral-900"
                      : "text-sr-neutral-500 hover:bg-sr-neutral-900 hover:text-sr-neutral-300"
                  }`}
                  aria-label="Line chart view"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M4 13L7.5 6L10 10L14 4"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setChartMode("candle")}
                  className={`sr-focus-ring rounded-md p-2 transition-colors ${
                    chartMode === "candle"
                      ? "text-sr-lemon-muted bg-sr-neutral-900"
                      : "text-sr-neutral-500 hover:bg-sr-neutral-900 hover:text-sr-neutral-300"
                  }`}
                  aria-label="Candlestick view"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <rect
                      x="3"
                      y="5"
                      width="3"
                      height="8"
                      rx="0.5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                    <rect
                      x="7.5"
                      y="3"
                      width="3"
                      height="12"
                      rx="0.5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                    <rect
                      x="12"
                      y="7"
                      width="3"
                      height="6"
                      rx="0.5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                  </svg>
                </button>
                <button
                  className="sr-focus-ring rounded-md p-2 text-sr-neutral-500 transition-colors hover:bg-sr-neutral-900 hover:text-sr-neutral-300"
                  aria-label="Analytics view"
                  type="button"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M3 3V15H15"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M6 12L9 8L12 10L15 5"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {candlestickData.length === 0 && (
            <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
              <span className="text-sm text-sr-neutral-500">No trade data yet</span>
            </div>
          )}
          <div ref={containerRef} className="h-full w-full cursor-crosshair" />
        </div>
      </div>
    </section>
  );
};
